import {
  Alert,
  Col,
  Container,
  ErrorText,
  FAB,
  FormikTextArea,
  Loading,
  Row,
  SectionTitle,
  Yup,
  useShowNotification
} from '@elotech/components';
import { Formik, FormikProps } from 'formik';
import DocumentosAvulsosList from 'itbi-common/components/documentacao/avulsos/DocumentosAvulsosList';
import { DeclaracaoService, UploadService } from 'itbi-common/service';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useHistory, useParams } from 'react-router';

type Props = {};

const initialValues: any = {
  motivoContestacao: '',
  arquivo: undefined,
  arquivoErrorMessage: undefined,
  nomeArquivo: undefined,
  valorProposto: 0
};

export const validationSchema = Yup.object().shape({
  motivoContestacao: Yup.string()
    .max(4000)
    .required()
    .label('Motivo Contestação')
});

const DeclaracaoContestacaoFormPage: React.FC<Props> = () => {
  const history = useHistory();
  const showNotification = useShowNotification();
  const [loading, setLoading] = useState(false);
  const [contestacao, setContestacao] = useState(initialValues);
  const [documentos, setDocumentos] = useState<any>([]);
  const [declaracaoItbi, setDeclaracaoItbi] = useState<any>({});

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    setLoading(true);
    DeclaracaoService.findDeclaracaoItbiById(id).then(
      ({ data: declaracao }) => {
        const documentosfiltered = declaracao.documentosAvulsos.filter(
          (doc: any) => doc.visivelCidadao && doc.arbitragem
        );
        setDeclaracaoItbi(declaracao);
        setDocumentos(documentosfiltered);
        setLoading(false);
      }
    );
  }, [id]);

  const onSaveContestacao = (contestacao: any) => {
    setLoading(true);
    DeclaracaoService.contestarDeclaracao(id, contestacao)
      .then(() => {
        setLoading(false);
        showNotification({
          level: 'success',
          message: `Contestação realizada com sucesso`
        });
        history.replace('/declaracoes-itbi');
      })
      .catch((error: any) => {
        setLoading(false);
        Alert.error({ title: `Erro ao contestar a declaração` }, error);
      });
  };

  const onSubmit = (contestacao: any) => {
    const { arquivo } = contestacao;
    if (!arquivo) {
      onSaveContestacao(contestacao);
      return;
    }

    setLoading(true);
    UploadService.getUrlUploadArquivoContestacao(id, arquivo[0].name)
      .then(urlResult => {
        return UploadService.uploadFileS3(urlResult.data.url, arquivo[0]);
      })
      .then(() => {
        onSaveContestacao({ ...contestacao, nomeArquivo: arquivo[0].name });
      })
      .catch(error => {
        Alert.error(
          {
            title: 'Erro ao fazer upload do arquivo da contestação'
          },
          error
        );
      })
      .finally(() => setLoading(false));
  };

  const onUploadFile = (event: any, formProps: FormikProps<any>) => {
    const { files } = event.target;

    const isValid = !(files && files[0] && files[0].size > 50000000);

    const errorObj = isValid
      ? { arquivoErrorMessage: undefined }
      : {
          arquivoErrorMessage: 'Arquivo maior que 50Mb'
        };

    setContestacao({
      motivoContestacao: formProps?.values?.motivoContestacao,
      arquivo: files,
      ...errorObj
    });
  };

  const onDownloadDocumentoAvulso = (documento: any) => {
    setLoading(true);

    return UploadService.getUrlDownloadArquivoAvulsoDeclaracao(
      id,
      documento.nome
    )
      .then(urlResult => UploadService.downloadFileS3(urlResult.data.url))
      .finally(() => setLoading(false));
  };

  return (
    <Container title="Contestação Declaração ITBI" icon="paste">
      <Loading loading={loading} />
      <SectionTitle>Documento para Contestação</SectionTitle>
      <Formik
        enableReinitialize
        initialValues={contestacao}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        render={(formProps: FormikProps<any>) => (
          <>
            <Row>
              <Col md={3}>
                <SectionTitle>Valor avaliado</SectionTitle>
                <NumberFormat
                  prefix="R$ "
                  decimalSeparator=","
                  thousandSeparator="."
                  decimalScale={2}
                  value={declaracaoItbi!.valorTransacaoAvaliacao}
                  fixedDecimalScale
                  disabled
                />
              </Col>
              <Col md={3}>
                <SectionTitle>Novo valor proposto</SectionTitle>
                <NumberFormat
                  prefix="R$ "
                  decimalSeparator=","
                  thousandSeparator="."
                  decimalScale={2}
                  allowNegative={false}
                  value={formProps.values.valorProposto}
                  name="valorProposto"
                  onValueChange={({ floatValue }) => {
                    formProps.setFieldValue('valorProposto', floatValue);
                  }}
                  fixedDecimalScale
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <SectionTitle> Motivo contestação:</SectionTitle>
              </Col>
              <FormikTextArea
                id="motivoContestacao"
                name="motivoContestacao"
                size={12}
              />
            </Row>
            <Row>
              <Col sm={6} className="form-group">
                <label htmlFor="upload-file-input">Arquivo</label>
                <div className="file-uploader">
                  <input
                    id="upload-file-input"
                    type="file"
                    className={`file-uploader-input${
                      contestacao?.arquivoErrorMessage ? ' error' : ''
                    }`}
                    title="Clique ou arraste para anexar"
                    onChange={value => onUploadFile(value, formProps)}
                  />

                  <label
                    htmlFor="upload-file-input"
                    className="input"
                    data-title={
                      contestacao?.arquivo && contestacao?.arquivo.length > 0
                        ? contestacao?.arquivo[0].name
                        : 'Clique ou arraste para anexar'
                    }
                  />

                  <label
                    htmlFor="upload-file-input"
                    className="file-uploader-icon"
                  />

                  {contestacao?.arquivoErrorMessage && (
                    <ErrorText>{contestacao?.arquivoErrorMessage}</ErrorText>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <DocumentosAvulsosList
                  documentos={documentos}
                  customSectionTitle={'Documentos Anexados na Arbitragem'}
                  onDownload={onDownloadDocumentoAvulso}
                  filter={(e: any) => e.arbitragem}
                />
              </Col>
            </Row>
            <div className="btn-save">
              <FAB
                icon="check"
                iconColor="white"
                title="Contestar"
                onClick={formProps.submitForm}
              />
            </div>
          </>
        )}
      />
    </Container>
  );
};

export default DeclaracaoContestacaoFormPage;
